import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Endpoint } from '@/Config/api/endpoint';
import { User } from '@/Domain/api/User/dto/User.dto';
import { useSupportWidgetStore } from '@/Domain/store/supportWidget.store';
import { SCENARIO_ID } from '@/context';

const body = document.querySelector('body');

const PROD_SCENARIO_ID = 'qj6hw2tua6';
const PROD_ASSISTANT_HOST = 'https://app.quickchat.ai';

const DEV_SCENARIO_ID = 'uof2azxlgp_dev';
const DEV_ASSISTANT_HOST = `https://${body?.getAttribute('api_server_url')}`;

const ASSISTANT_SCENARIO_ID = import.meta.env.DEV ? DEV_SCENARIO_ID : PROD_SCENARIO_ID;
const ASSISTANT_HOST = import.meta.env.DEV ? DEV_ASSISTANT_HOST : PROD_ASSISTANT_HOST;

const WIDGET_ORIGIN = import.meta.env.VITE_WIDGET_URL || 'https://bubble.quickchat.ai';

export const SupportWidget = () => {
  const queryClient = useQueryClient();

  const myself = queryClient.getQueryData<User>([Endpoint.user.myself]);
  const { open, toggleOpen, pendingMessage, clearPendingMessage } = useSupportWidgetStore();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const clientMetadata = useMemo(() => {
    if (myself) {
      const visitor_name = [myself.first_name, myself.last_name].filter(Boolean).join(' ').trim();

      return {
        visitor_name: visitor_name || '',
        visitor_email: myself.email,
        visitor_scenario_id: SCENARIO_ID,
      };
    }

    return null;
  }, [myself]);

  const config = useMemo(() => {
    if (!clientMetadata) return null;

    const params = new URLSearchParams({
      scenarioId: ASSISTANT_SCENARIO_ID,
      viewType: 'embed',
      showCloseButton: 'true',
      // Suppress the widget's autofocus on iframe load. The Support AI iframe
      // is mounted globally by `DefaultLayout` and finishes loading 1-2s after
      // any page-level autofocus (e.g. the Getting Started URL input), at
      // which point its `MessageInput` would steal focus from the parent app.
      autoFocus: 'false',
      ...(ASSISTANT_HOST ? { host: ASSISTANT_HOST } : {}),
      params: JSON.stringify(clientMetadata),
    });

    return `${WIDGET_ORIGIN}/widget.html?${params.toString()}`;
  }, [clientMetadata]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.type === 'quickchat_minimize' &&
        event.source === iframeRef.current?.contentWindow
      ) {
        toggleOpen();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [toggleOpen]);

  useEffect(() => {
    if (!open || !pendingMessage || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const sendMessage = () => {
      iframe.contentWindow?.postMessage(
        { type: 'quickchat_send_message', data: { message: pendingMessage } },
        WIDGET_ORIGIN,
      );
      clearPendingMessage();
    };

    if (iframe.dataset.loaded) {
      sendMessage();
    } else {
      iframe.addEventListener('load', sendMessage, { once: true });
      return () => iframe.removeEventListener('load', sendMessage);
    }
  }, [open, pendingMessage, clearPendingMessage]);

  const handleIframeLoad = useCallback(() => {
    if (iframeRef.current) {
      iframeRef.current.dataset.loaded = 'true';
    }
  }, []);

  if (!myself || !clientMetadata || !config) return null;

  return (
    <iframe
      ref={iframeRef}
      id="feedback-quickchat-widget"
      title="Feedback QuickChat Widget"
      src={config}
      onLoad={handleIframeLoad}
      allow="microphone; local-network-access"
      referrerPolicy="strict-origin"
      className={`fixed bottom-0 right-0 z-50 m-[16px] h-[calc(100%-75px)] w-full max-w-[420px] rounded-xl border border-border bg-card duration-300 shadow-xl ${
        open
          ? 'ease-in-out translate-x-0 opacity-100 scale-100 visible'
          : 'ease-in-out translate-x-full opacity-70 scale-20 invisible'
      }`}
    />
  );
};
